import React, { useEffect, useState, useMemo, useCallback } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { fetchApplications } from "../../../store/reducers/applicationSlice";
import { showSuccess, showError } from '../../../helpers/toast';
import Navbar from "../../navbar";
import Header from "../../header";
import Pagination from "../dashboard/appPagination";
import iconSearch from "../../../assets/media/icon-search.svg";
import DatePicker from "react-datepicker";

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper();

const BAHAMAS_TZ = "America/Nassau";

const formatBahamasDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-CA", {
    timeZone: BAHAMAS_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const AppProcessApp = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const authToken = user.access_token;
  const applications = useSelector((state) => state.applications);
  const getallUserdata = useSelector((state) => state.userdata.getalluserdata);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [searchBy, setSearchBy] = useState("applicationId");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filteredApplication, setFilteredApplication] = useState([]);
  const [downloadingIds, setDownloadingIds] = useState([]);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const handleSearchByChange = (e) => {
    setSearchBy(e.target.value);
    setSearchTerm("");
  };

  // debounced search
  useEffect(() => {
    const handler = setTimeout(() => {
      let term = searchTerm;
      if (searchBy === "applicationId" && term.startsWith("GB-")) {
        term = term.replace("GB-", "");
      }
      setDebouncedSearchTerm(term);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm, searchBy]);

  // Fetch applications when date filter changes
  useEffect(() => {
    let queryParams = {
      state: 'in_process' // Add state filter to API call
    };

    // SECURITY: Add company_id filter at API level for company_admin
    if (user.user?.user_role === "company_admin" && user.user?.company_id) {
      queryParams.company_id = user.user.company_id;
    }
    // SECURITY: Add user_id filter at API level for employees
    // EXCEPTION: User reported search does not work when user_id is passed
    else if (user.user?.user_role === "employee" && user.user?.id && !debouncedSearchTerm) {
      queryParams.user_id = user.user.id;
    }

    if (startDate) {
      queryParams.createdFrom = moment(startDate).format("YYYY-MM-DD");
    }
    if (endDate) {
      queryParams.createdTo = moment(endDate).format("YYYY-MM-DD");
    }

    // Add search parameters if debouncedSearchTerm is present
    if (debouncedSearchTerm) {
      queryParams[searchBy] = debouncedSearchTerm;
    }

    dispatch(fetchApplications({
      accessToken: authToken,
      queryParams,
      fetchAllPages: true // Fetch all pages to show all applications
    }));
  }, [authToken, dispatch, startDate, endDate, user.user?.id, user.user?.user_role, user.user?.company_id, debouncedSearchTerm, searchBy]);

  // Merge user data + filter search
  useEffect(() => {

    if (applications.data?.length > 0) {
      // Debug: Check what states are in the data
      const statesInData = [...new Set(applications.data.map(app => app.state))];

      // Debug: Check the structure of the first application item
      if (applications.data?.length > 0) {
      }

      const mergedData = applications.data.map((appItem) => {
        // Try multiple possible property names for user_id
        const userIdFromApp = appItem.user_id || appItem.userId || appItem.user?.id;

        const userItem = getallUserdata?.find(
          (userData) => userData.id === userIdFromApp
        );

        // Use userItem.id if found, otherwise use userIdFromApp, or fallback to undefined
        const finalUserId = userItem?.id || userIdFromApp;

        if (userItem) {
          return {
            ...appItem,
            applicationId: appItem.id,
            ...userItem,
            user_id: finalUserId, // Use the final determined user_id
            userId: userItem.id,
            // Keep application dates (userItem can overwrite created_at/updated_at)
            created_at: appItem.created_at,
            updated_at: appItem.updated_at,
          };
        }
        return {
          ...appItem,
          applicationId: appItem.id,
          user_id: finalUserId, // Use the final determined user_id
          company_id: appItem.company_id || user.user?.company_id // Fallback to user's company
        };
      });

      // Filter by role, company, and state
      const companyAndStateFiltered = mergedData.filter((item) => {
        const hasCorrectState = item.state === "in_process";
        const userRole = user.user?.user_role;
        const currentUserId = user.user?.id;
        const itemUserId = item.user_id || item.userId || item.user?.id;

        let shouldShow = false;

        const itemCompanyId = item.company_id || item.companyId;

        if (userRole === "company_admin") {
          // Company admin can see all applications in their company
          const hasCorrectCompany = String(itemCompanyId) === String(user.user.company_id);
          shouldShow = hasCorrectCompany && hasCorrectState;
        } else if (userRole === "employee") {
          // Employee can only see their own applications within their company
          // Compare as strings to handle type mismatches
          const isOwnApplication = String(itemUserId) === String(currentUserId);
          const hasCorrectCompany = String(itemCompanyId) === String(user.user.company_id);
          shouldShow = isOwnApplication && hasCorrectCompany && hasCorrectState;

          // Debug for employees
          if (!shouldShow && hasCorrectState) {
          }
        } else {
          // Default: filter by company (fallback)
          const hasCorrectCompany = String(itemCompanyId) === String(user.user.company_id);
          shouldShow = hasCorrectCompany && hasCorrectState;
        }

        return shouldShow;
      });

      // Then apply search filter
      const filteredData = companyAndStateFiltered.filter((item) => {
        // If server-side search is active (debouncedSearchTerm is set), 
        // rely on the API results and skip strict local text filtering.
        if (debouncedSearchTerm && (searchTerm || searchQuery)) return true;
      if (!searchQuery && !searchTerm) return true; // Show all if no search query

        const term = searchQuery || searchTerm;
        return Object.values(item).some(
          (value) =>
            value &&
            value.toString().toLowerCase().includes(term.toLowerCase())
        );
      });

      setFilteredApplication(filteredData);
      setCurrentPage(1); // Reset to first page when data changes
    }
  }, [searchQuery, applications.data, getallUserdata, user.user]);

  const handleSearchInputChange = (e) => {
    let val = e.target.value;
    if (searchBy === "applicationId") {
      const digits = val.replace(/\D/g, "");
      if (digits.length > 0) {
        val = `GB-${digits}`;
      } else {
        val = "";
      }
    }
    setSearchQuery(val);
    setSearchTerm(val);
  };

  const getTotalPages = () => {
    return Math.ceil(filteredApplication?.length / itemsPerPage);
  };

  const handleDownload = useCallback(async (item) => {
    const { applicationId, userId } = item;

    // Prevent multiple clicks
    if (downloadingIds.includes(applicationId)) {
      return;
    }

    setDownloadingIds((prev) => [...prev, applicationId]);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}file/${userId}/downloadapplicationfiles/${applicationId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/zip",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `application_${applicationId}_files.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showSuccess('Files downloaded successfully!');
    } catch (error) {
      console.error("Download failed", error);
      showError(`Download failed: ${error.message}`);
    } finally {
      setDownloadingIds((prev) => prev.filter((id) => id !== applicationId));
    }
  }, [authToken, downloadingIds]);

  // Pagination + sorting data
  const paginatedData = useMemo(() => {
    return filteredApplication
      .sort((a, b) => b.applicationId - a.applicationId)
      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  }, [filteredApplication, currentPage, itemsPerPage]);

  // React Table columns
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "serial",
        header: "S.NO.",
        cell: (info) => (currentPage - 1) * itemsPerPage + info.row.index + 1,
      }),
      columnHelper.accessor("applicationId", {
        header: "Application No.",
        cell: (info) => `GB-${info.getValue()}`,
      }),
      columnHelper.display({
        id: "applicationName",
        header: "Application Name",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong title={row.name || row.applicationName}>
                {(row.name || row.applicationName || "")?.length > 50
                  ? `${(row.name || row.applicationName || "").substring(0, 50)}...`
                  : (row.name || row.applicationName || 'N/A')}
              </strong>
              <span>{row.categoryName || 'N/A'}</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "submittedBy",
        header: "Submitted By",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong>{row.companyName || row.company_name || 'N/A'}</strong>
              <span>{row.companyEmail || row.email || 'N/A'}</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "submittedFor",
        header: "Submitted For",
        cell: (info) => {
          const row = info.row.original;
          const userId = row.user_id || row.userId || row.user?.id;
          const user = getallUserdata?.find(
            (user) => user.id === userId
          );
          const userEmail = user?.email || user?.userEmail || user?.user_email || 'N/A';
          return (
            <div className="boardTitle">
              <strong>{row.userName || row.user_name || row.name || 'N/A'}</strong>

              <span>{row.userEmail || 'N/A'}</span>
            </div>
          );
        },
      }),
      columnHelper.accessor("created_at", {
        header: "Submitted Date",
        cell: (info) => (
          <span className="dateText">{formatBahamasDate(info.getValue())}</span>
        ),
      }),
      columnHelper.display({
        id: "actions",
        header: () => <div className="text-center">Download</div>,
        cell: (info) => {
          const row = info.row.original;
          const isDownloading = downloadingIds.includes(row.applicationId);
          return (
            <div className="boardActions">
              <button
                className="btn btnDownload"
                type="button"
                onClick={() => {
                  if (!isDownloading) {
                    handleDownload(row);
                  }
                }}
                disabled={isDownloading}
                style={{
                  opacity: isDownloading ? 0.6 : 1,
                  cursor: isDownloading ? 'not-allowed' : 'pointer',
                  minWidth: '120px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {isDownloading ? 'Downloading...' : 'Download'}
              </button>
            </div>
          );
        },
      }),
    ],
    [downloadingIds, currentPage, itemsPerPage, handleDownload, getallUserdata]
  );

  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Add loading state
  if (applications.loading) {
    return (
      <div className="mainApp">
        <Navbar />
        <Header />
        <div className="wrapper">
          <div className="boxAppBoard boxWhite">
            <div className="text-center p-4">
              <div className="loader"></div>
              <p>Loading in-process applications...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // // Show message if no data is available
  // if (!applications.data || applications.data?.length === 0) {
  //   return (
  //     <div className="mainApp">
  //       <Navbar />
  //       <Header />
  //       <div className="wrapper">
  //         <div className="boxAppBoard boxWhite">
  //           <div className="text-center p-4">
  //             <div className="spinner"></div>
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div className="mainApp">
      <Navbar />
      <Header />
      <div className="wrapper">
        <div className="boxAppBoard boxWhite">
          {/* Header with search + date filter */}
          <div className="boardHeader">
            <div className="boardHeadLeft">
              <h2>In Process Applications</h2>
            </div>
            <div className="boardHeadRight">
              <select onChange={handleSearchByChange} value={searchBy} >
                <option value="applicationId">Search by Application ID</option>
                <option value="username">Search by Employee Name</option>

              </select>
              <form className="searchForm">
                <input
                  type="text"
                  placeholder="Search"
                  onChange={handleSearchInputChange}
                  value={searchTerm}
                  className="searchInput"
                />
                <button type="button" className="searchBtn">
                  <img src={iconSearch} alt="search" />
                </button>
              </form>
              <div className="datePicker">
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => {
                    const [start, end] = update;
                    setStartDate(start);
                    setEndDate(end);
                    setCurrentPage(1);
                  }}
                  isClearable={true}
                  placeholderText="Select Date Range"
                  className="date-range-input"
                  dateFormat="dd/MM/yyyy"
                />
              </div>
            </div>
          </div>

          <div className="boardContent table-responsive">
            <table className="table">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr className="" key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody>
                {table.getRowModel().rows?.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr className="" key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={table.getHeaderGroups()?.[0]?.headers.length} style={{ textAlign: "center", padding: "20px" }}>
                      <p>No in-process applications found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={getTotalPages}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
          />
        </div>
      </div>
    </div>
  );
};

export default AppProcessApp;
